import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface ExpoPerformanceReportHistoryItem {
  reportId: string;
  expoId: string;
  expoCode: string;
  expoName: string;
  snapshotVersion: number;
  reportRevision: number;
  reportStatus: string;
  deliveryStatus: string;
  generatedAt: string;
  sentAt?: string | null;
  recipients: string[];
  ccRecipients: string[];
  fileName?: string | null;
  spreadsheetFileName?: string | null;
  failureDetails?: string | null;
  canDownload: boolean;
  canResend: boolean;
  canRegenerate: boolean;
}

export interface ExpoPerformanceVarianceDetail {
  type: string;
  reference: string;
  amount: number;
  status: string;
  reason?: string | null;
}

export interface ExpoPerformancePayload {
  expoId: string;
  expoCode: string;
  expoName: string;
  expoStatus: string;
  closedAtUtc?: string | null;
  snapshotVersion: number;
  reportRevision: number;
  isFinal: boolean;
  calculationVersion: string;
  summary: {
    grossSales: number;
    netSales: number;
    approvedCollections: number;
    outstandingReceivables: number;
    collectionRatePercent: number;
    expectedStockPieces: number;
    physicalStockPieces: number;
    stockVariancePieces: number;
    sellThroughRatePercent: number;
  };
  financialVarianceDetails: ExpoPerformanceVarianceDetail[];
  stockVarianceDetails: ExpoPerformanceVarianceDetail[];
  approvalsAndAcknowledgements: string[];
  calculatedAtUtc: string;
}

export interface ExpoPerformanceReportDeliveryAttempt {
  id: string;
  reportId: string;
  recipientEmail: string;
  deliveryType: string;
  status: string;
  retryNumber: number;
  attemptedAt: string;
  deliveredAt?: string | null;
  failureReason?: string | null;
}

export interface ExpoPerformanceReportDetail {
  report: ExpoPerformanceReportHistoryItem;
  snapshot: ExpoPerformancePayload;
  deliveryAttempts: ExpoPerformanceReportDeliveryAttempt[];
}

const parseFileName = (contentDisposition?: string) => {
  if (!contentDisposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) return decodeURIComponent(utf8[1].replace(/"/g, ""));
  const ascii = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return ascii?.[1] ?? null;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const expoPerformanceReportsApi = {
  getHistory: async (expoId?: string) => {
    const { data } = await apiClient.get<ApiResponse<ExpoPerformanceReportHistoryItem[]>>(
      "/expo-performance-reports/history",
      { params: { expoId } }
    );
    return data.data ?? [];
  },

  getReport: async (reportId: string) => {
    const { data } = await apiClient.get<ApiResponse<ExpoPerformanceReportDetail>>(
      `/expo-performance-reports/${reportId}`
    );
    return data.data;
  },

  downloadPdf: async (reportId: string) => {
    const response = await apiClient.get(`/expo-performance-reports/${reportId}/download/pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    const fileName = parseFileName(response.headers["content-disposition"]) ?? `expo-performance-${reportId}.pdf`;
    triggerBlobDownload(response.data, fileName);
  },

  downloadSpreadsheet: async (reportId: string) => {
    const response = await apiClient.get(`/expo-performance-reports/${reportId}/download/spreadsheet`, {
      responseType: "blob",
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    });
    const fileName = parseFileName(response.headers["content-disposition"]) ?? `expo-performance-${reportId}.xlsx`;
    triggerBlobDownload(response.data, fileName);
  },

  regenerate: async (reportId: string, reason?: string) => {
    const { data } = await apiClient.post<ApiResponse<ExpoPerformanceReportHistoryItem>>(
      `/expo-performance-reports/${reportId}/regenerate`,
      { reason: reason ?? null }
    );
    return data.data;
  },

  resend: async (reportId: string) => {
    const { data } = await apiClient.post<ApiResponse<ExpoPerformanceReportHistoryItem>>(
      `/expo-performance-reports/${reportId}/resend`
    );
    return data.data;
  },
};
